// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — the play surface: the plate, the armed ghost, the tray and the action row.
//
// PICK, AIM, SET. Tap a chip to arm a fitting; every legal cell tints at once and the ghost lands on
// the best one, so you never face a blank plate wondering where to start. Then tap or drag to move
// it, rotate with a button that only cycles rotations that are legal WHERE THE GHOST IS, and commit
// with the one control that writes anything.
//
// Two fleet lessons are wired in rather than commented at:
//   - Hit-testing reads getBoundingClientRect at POINTERDOWN, never inside a rAF draw. Geometry
//     computed in the draw loop silently drops every tap before the first frame.
//   - Pointer position is clientX/clientY minus that rect, never offsetX/offsetY, which scales
//     oddly under devicePixelRatio and page zoom.
//
// THE GHOST IS OFFSET ABOVE THE FINGER ON TOUCH, and the offset direction is decided ONCE at
// pointerdown rather than re-evaluated as you drag. A mid-drag flip is a discontinuity of twice the
// offset — two or three whole cells — crossed while the finger moves one pixel, which reads as the
// piece teleporting. Hit-testing uses the SAME offset point the ghost is drawn at, so what you see
// under the ghost is what you get when you release.

import { FORMS, type PieceId } from './pieces';
import { PIECES } from './pieces';
import { SIZE, VENTS, sidesAt, type Plate } from './plate';
import { hasBothGauges, pathsFor, CULVERT_W, CULVERT_BORE, FILAMENT_GAP, FILAMENT_W } from './draw';
import { MIN_TARGET } from './layout';

export interface Ghost {
  slot: number;
  piece: PieceId;
  rot: number;
  x: number;
  y: number;
  legal: boolean;
  /** Weeps this placement would cost (Seep). */
  cost: number;
}

export interface TrayChip {
  slot: number;
  piece: PieceId;
  armed: boolean;
  spent: boolean;
  /** Face-down in Seep until it is revealed. */
  hidden: boolean;
}

export interface TermChip {
  key: string;
  label: string;
  value: number;
  /** Rendered in the warning ink when the debt is growing. */
  bad?: boolean;
}

export interface Paint {
  plate: Plate;
  ghost: Ghost | null;
  /** Cells legal for the armed fitting, and what each would cost. */
  legalCells: Map<number, number>;
  /** Vent id -> 0 unsealed, 1 sealed by you, 2 sealed by a rival first. */
  vents: Uint8Array;
  round: number;
  rounds: number;
  spent: number;
  score: number;
  par: number | null;
  terms: TermChip[];
  tray: TrayChip[];
  rots: number[];
  rotIndex: number;
  canSet: boolean;
  setLabel: string;
  /** Plain-words reason the SET button is disabled. This is the game's only teaching surface. */
  reason: string;
  canUndo: boolean;
  note: string;
  /** Cells written by the last placement, for the settle animation. */
  fresh: number[];
  /** Live rivals: name, glyph, marks placed, score. */
  rivals: Array<{ name: string; glyph: string; ink: string; marks: number; score: number; away: boolean; you: boolean }>;
}

export interface ViewDeps {
  onArm: (slot: number) => void;
  onAim: (x: number, y: number) => void;
  onRotate: () => void;
  onSet: () => void;
  onUndo: () => void;
  onHelp: () => void;
  onQuit: () => void;
  onNudge: (dx: number, dy: number) => void;
}

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

const SVG_NS = 'http://www.w3.org/2000/svg';

export class View {
  readonly root: HTMLElement;
  private plateEl: HTMLElement;
  private cellEls: SVGSVGElement[] = [];
  private ghostEl: HTMLElement;
  private deps: ViewDeps;
  private dragId: number | null = null;
  /** Decided at pointerdown and never re-evaluated during the drag — see the file header. */
  private aimOffset = 0;
  private coarse = false;

  constructor(deps: ViewDeps) {
    this.deps = deps;
    this.coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    this.root = document.createElement('div');
    this.root.className = 'stage';
    this.root.innerHTML = `
      <div class="topbar">
        <div class="tb-l"><span class="roundpill" id="roundp"></span><span class="marks" id="marks"></span></div>
        <div class="tb-r">
          <span class="score" id="score">0</span><span class="par" id="par"></span>
          <button class="iconbtn" id="helpBtn" type="button" aria-label="How to play">?</button>
          <button class="iconbtn" id="quitBtn" type="button" aria-label="Leave">✕</button>
        </div>
      </div>
      <div class="terms" id="terms"></div>
      <div class="platewrap"><div class="plate" id="plate" role="grid" aria-label="Your plate"></div></div>
      <div class="rivals" id="rivals"></div>
      <p class="note" id="note" aria-live="polite"></p>
      <div class="tray" id="tray" role="group" aria-label="Fittings this round"></div>
      <div class="actions">
        <button class="ctl" id="undoBtn" type="button" aria-label="Undo">↶</button>
        <button class="setbtn" id="setBtn" type="button">Set</button>
        <button class="ctl" id="rotBtn" type="button" aria-label="Rotate">⟳<span class="rotn" id="rotn"></span></button>
      </div>`;

    this.plateEl = this.root.querySelector('#plate')!;
    this.plateEl.style.setProperty('--n', String(SIZE));
    for (let i = 0; i < SIZE * SIZE; i++) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('viewBox', '0 0 100 100');
      svg.setAttribute('class', 'cell');
      svg.setAttribute('role', 'gridcell');
      this.plateEl.append(svg);
      this.cellEls.push(svg);
    }
    this.ghostEl = document.createElement('div');
    this.ghostEl.className = 'ghost';
    this.ghostEl.setAttribute('aria-hidden', 'true');
    this.plateEl.append(this.ghostEl);
    this.buildVents();
    this.wire();
  }

  /** The twelve vents are printed furniture — built once, then only their state class changes. */
  private ventEls: HTMLElement[] = [];
  private buildVents(): void {
    for (const v of VENTS) {
      const el = document.createElement('div');
      el.className = 'vent';
      el.style.setProperty('--vx', String(v.x));
      el.style.setProperty('--vy', String(v.y));
      el.dataset.dir = String(v.dir);
      el.dataset.gauge = String(v.gauge);
      el.setAttribute('aria-hidden', 'true');
      this.plateEl.append(el);
      this.ventEls.push(el);
    }
  }

  private cellFromPointer(e: PointerEvent): { x: number; y: number } {
    const r = this.plateEl.getBoundingClientRect();
    const cell = r.width / SIZE;
    // The SAME offset the ghost is drawn at, so the preview and the landing cell can never disagree.
    const px = e.clientX - r.left;
    const py = e.clientY - r.top + this.aimOffset;
    return {
      x: clamp(Math.floor(px / cell), 0, SIZE - 1),
      y: clamp(Math.floor(py / cell), 0, SIZE - 1),
    };
  }

  private wire(): void {
    const d = this.deps;
    this.plateEl.addEventListener('pointerdown', (e) => {
      if (this.dragId !== null) return;
      this.dragId = e.pointerId;
      try {
        this.plateEl.setPointerCapture(e.pointerId);
      } catch {
        /* capture is an optimisation, not a requirement */
      }
      // Touch only, and fixed for the whole gesture. A mouse gets no offset at all — a ghost floating
      // an inch above a precise cursor is nonsense.
      if (this.coarse && e.pointerType !== 'mouse') {
        const r = this.plateEl.getBoundingClientRect();
        const cell = r.width / SIZE;
        const inTopQuarter = e.clientY - r.top < r.height * 0.25;
        this.aimOffset = inTopQuarter ? cell : -cell;
      } else {
        this.aimOffset = 0;
      }
      this.root.classList.add('aiming');
      const { x, y } = this.cellFromPointer(e);
      d.onAim(x, y);
    });
    this.plateEl.addEventListener('pointermove', (e) => {
      if (this.dragId !== e.pointerId) return;
      const { x, y } = this.cellFromPointer(e);
      d.onAim(x, y);
    });
    const end = (e: PointerEvent): void => {
      if (this.dragId !== e.pointerId) return;
      this.dragId = null;
      this.aimOffset = 0;
      this.root.classList.remove('aiming');
    };
    this.plateEl.addEventListener('pointerup', end);
    // A cancelled pointer is an ABORTED gesture, not a drop. The ghost stays where it was; nothing
    // was committed, so there is nothing to undo.
    this.plateEl.addEventListener('pointercancel', end);

    this.root.querySelector('#rotBtn')!.addEventListener('click', () => d.onRotate());
    this.root.querySelector('#setBtn')!.addEventListener('click', () => d.onSet());
    this.root.querySelector('#undoBtn')!.addEventListener('click', () => d.onUndo());
    this.root.querySelector('#helpBtn')!.addEventListener('click', () => d.onHelp());
    this.root.querySelector('#quitBtn')!.addEventListener('click', () => d.onQuit());
  }

  /** Desktop keys. A full keyboard path is a WCAG 2.1.1 / 2.5.7 requirement, not a nicety. */
  handleKey(e: KeyboardEvent): boolean {
    const d = this.deps;
    if (e.key >= '1' && e.key <= '4') {
      d.onArm(Number(e.key) - 1);
      return true;
    }
    switch (e.key) {
      case 'ArrowLeft': d.onNudge(-1, 0); return true;
      case 'ArrowRight': d.onNudge(1, 0); return true;
      case 'ArrowUp': d.onNudge(0, -1); return true;
      case 'ArrowDown': d.onNudge(0, 1); return true;
      case 'r': case 'R': d.onRotate(); return true;
      case 'z': case 'Z': d.onUndo(); return true;
      case 'Enter': case ' ': d.onSet(); return true;
      case '?': d.onHelp(); return true;
      default: return false;
    }
  }

  render(p: Paint): void {
    // ── the plate ────────────────────────────────────────────────────────────────────────────
    for (let i = 0; i < this.cellEls.length; i++) {
      const x = i % SIZE;
      const y = Math.floor(i / SIZE);
      const s = sidesAt(p.plate, x, y);
      const pi = p.plate.piece[i];
      const key = s ? `${pi}:${p.plate.rot[i]}` : 'e';
      const cost = p.legalCells.get(i);
      const tint = cost === undefined ? '' : cost > 0 ? ' costly' : ' legal';
      const fresh = p.fresh.includes(i) ? ' fresh' : '';
      const el = this.cellEls[i];
      const want = key + tint + fresh;
      if (el.dataset.k !== want) {
        el.dataset.k = want;
        el.setAttribute('class', `cell${tint}${fresh}`);
        el.replaceChildren();
        if (s) el.insertAdjacentHTML('afterbegin', svgFor(s, pi));
      }
    }

    // ── the ghost ────────────────────────────────────────────────────────────────────────────
    if (p.ghost) {
      const g = p.ghost;
      this.ghostEl.hidden = false;
      this.ghostEl.className = `ghost${g.legal ? '' : ' bad'}${g.cost > 0 ? ' costly' : ''}`;
      this.ghostEl.style.setProperty('--gx', String(g.x));
      this.ghostEl.style.setProperty('--gy', String(g.y));
      const sides = FORMS[g.piece][g.rot];
      const want = `${g.piece}:${g.rot}:${g.legal}:${g.cost}`;
      if (this.ghostEl.dataset.k !== want) {
        this.ghostEl.dataset.k = want;
        this.ghostEl.innerHTML = `<svg viewBox="0 0 100 100">${svgFor(sides, pieceIdx(g.piece))}</svg>`;
      }
    } else {
      this.ghostEl.hidden = true;
    }

    for (let i = 0; i < this.ventEls.length; i++) {
      const st = p.vents[i] ?? 0;
      const el = this.ventEls[i];
      const cls = `vent s${st}`;
      if (el.className !== cls) el.className = cls;
    }

    // ── chrome ───────────────────────────────────────────────────────────────────────────────
    this.set('#roundp', `Round ${p.round + 1}/${p.rounds}`);
    this.set('#marks', `${p.spent}/4`);
    this.set('#score', String(p.score));
    this.set('#par', p.par === null ? '' : `par ${p.par}`);
    this.set('#note', p.note);

    const terms = this.root.querySelector('#terms')!;
    const tkey = p.terms.map((t) => `${t.key}${t.value}${t.bad ? '!' : ''}`).join('|');
    if (terms.getAttribute('data-k') !== tkey) {
      terms.setAttribute('data-k', tkey);
      terms.innerHTML = p.terms
        .map((t) => `<span class="term${t.bad ? ' bad' : ''}"><b>${esc(t.label)}</b><i>${t.value}</i></span>`)
        .join('');
    }

    const tray = this.root.querySelector('#tray')!;
    const trayKey = p.tray.map((c) => `${c.slot}${c.piece}${c.armed}${c.spent}${c.hidden}`).join('|');
    if (tray.getAttribute('data-k') !== trayKey) {
      tray.setAttribute('data-k', trayKey);
      tray.innerHTML = p.tray
        .map((c) =>
          c.hidden
            ? `<span class="chip hidden" aria-hidden="true"><span class="chipq">?</span></span>`
            : `<button class="chip${c.armed ? ' armed' : ''}${c.spent ? ' spent' : ''}" type="button"
                 data-slot="${c.slot}"${c.spent ? ' disabled' : ''}
                 aria-pressed="${c.armed}" aria-label="${esc(PIECES[c.piece].name)}">
                 <svg viewBox="0 0 100 100" aria-hidden="true">${svgFor(FORMS[c.piece][0], pieceIdx(c.piece))}</svg>
               </button>`,
        )
        .join('');
      tray.querySelectorAll<HTMLElement>('[data-slot]').forEach((b) =>
        b.addEventListener('click', () => this.deps.onArm(Number(b.dataset.slot))),
      );
    }

    const rivals = this.root.querySelector('#rivals')!;
    const rk = p.rivals.map((r) => `${r.name}${r.marks}${r.score}${r.away}`).join('|');
    if (rivals.getAttribute('data-k') !== rk) {
      rivals.setAttribute('data-k', rk);
      rivals.innerHTML = p.rivals
        .map(
          (r) =>
            `<span class="rival${r.you ? ' you' : ''}${r.away ? ' away' : ''}" title="${esc(r.name)}">
               <i style="color:${r.ink}">${r.glyph}</i><b>${esc(r.name)}</b><u>${r.score}</u></span>`,
        )
        .join('');
    }

    const rot = this.root.querySelector<HTMLButtonElement>('#rotBtn')!;
    rot.hidden = p.rots.length <= 1;
    this.set('#rotn', p.rots.length > 1 ? `${p.rotIndex + 1}/${p.rots.length}` : '');

    const undo = this.root.querySelector<HTMLButtonElement>('#undoBtn')!;
    undo.disabled = !p.canUndo;

    const set = this.root.querySelector<HTMLButtonElement>('#setBtn')!;
    set.textContent = p.setLabel;
    set.disabled = !p.canSet;
    // The disabled SET button is the only place the rules are ever explained, so its reason has to
    // be on screen rather than in a title attribute nobody on a phone can reach.
    set.title = p.canSet ? '' : p.reason;
  }

  private set(sel: string, text: string): void {
    const el = this.root.querySelector(sel);
    if (el && el.textContent !== text) el.textContent = text;
  }

  destroy(): void {
    this.root.remove();
  }
}

const PIECE_LIST: readonly PieceId[] = ['cs', 'ce', 'ct', 'fs', 'fe', 'ft', 'cp', 'bp'];
const pieceIdx = (id: PieceId): number => PIECE_LIST.indexOf(id);

/** Render one cell's line-work. Kept out of the class so a test can call it with no DOM. */
export function svgFor(sides: Parameters<typeof pathsFor>[0], pieceIndex: number): string {
  const isCoupler = pieceIndex === 6;
  const isBypass = pieceIndex === 7;
  const p = pathsFor(sides, isCoupler, isBypass);
  const out: string[] = [];
  for (const d of p.culvertBand) {
    out.push(`<path d="${d}" class="c-band" stroke-width="${CULVERT_W}" fill="none" stroke-linecap="butt"/>`);
  }
  for (const d of p.culvertBore) {
    out.push(`<path d="${d}" class="c-bore" stroke-width="${CULVERT_BORE}" fill="none" stroke-linecap="butt"/>`);
  }
  for (const d of p.filament) {
    // Two hairlines either side of the centre-line, drawn as one stroked path with a wide dash-free
    // outline plus an inner gap — cheaper and crisper than computing true parallel offsets.
    out.push(`<path d="${d}" class="f-outer" stroke-width="${FILAMENT_GAP + FILAMENT_W}" fill="none"/>`);
    out.push(`<path d="${d}" class="f-inner" stroke-width="${FILAMENT_GAP - FILAMENT_W}" fill="none"/>`);
  }
  if (p.bead) out.push(`<circle cx="${p.bead[0]}" cy="${p.bead[1]}" r="5" class="f-bead"/>`);
  if (isCoupler && hasBothGauges(sides)) {
    out.push(`<circle cx="50" cy="50" r="13" class="coupler"/>`);
  }
  return out.join('');
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

export { MIN_TARGET };
